import { FormSkeleton, SelectOption } from '@globaleris/ui.library'
import { List, ListItem, Typography, makeStyles, useTheme } from '@material-ui/core'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { EnumCurrency } from '../../../../../api'
import { ContractSummaryModel } from '../../../../../api/Argentia/endpoints/contracts/model/contracts'
import { EnumCommodity } from '../../../../../api/Argentia/enums'
import { EnumNivelDesglose } from '../../../../../api/Argentia/enums/enumNivelDesglose'
import { EnumProrrateo } from '../../../../../api/Argentia/enums/enumProrrateo'
import { ProductModel } from '../../../../../api/Argentia/model/contracts'
import { enumTranslateOption } from '../../../../../helpers'
import { selectOptionValueToLabel } from '../../../../../helpers/selectOptionHelpers'
import TemplateSettings from '../../../../templateSettings/templateSettings.model'
import { styles } from '../../productsEdit.styles'
import { TemplateListItem } from '../TemplateListItem/templateListItem'

export interface ProductDetailPageProps {
  product: ProductModel | undefined
  productTypeOptions?: SelectOption[]
  templates?: TemplateSettings[]
  templatesOptions?: SelectOption[]
  templateConceptsOptions?: SelectOption[]
  productTemplates?: SelectOption[]
  productTemplatesConcepts?: SelectOption[]
  contractData?: ContractSummaryModel & { commodity: EnumCommodity }
  isLoading?: boolean
}

export const ProductDetail: React.FC<ProductDetailPageProps> = ({
  product,
  productTypeOptions,
  templates,
  templatesOptions,
  templateConceptsOptions,
  productTemplates,
  productTemplatesConcepts,
  contractData,
  isLoading
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'productsEdit', 'enums'])
  const history = useHistory()
  // Import Styles
  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()

  // Handlers Starts
  // Handlers Ends

  // Form Definition Starts
  // Form Definition Ends

  // DataTable Config Starts
  // DataTable Config Ends

  // Functions Starts

  // Check if products or productTypeOptions is empty
  // const productIsEmpty =
  //   !product || (Object.keys(product).length === 0 && product.constructor === Object)

  // const productTypeOptionsIsEmpty =
  //   !productTypeOptions ||
  //   (Object.keys(productTypeOptions).length === 0 && productTypeOptions.constructor === Object)

  // Functions Ends

  // Hooks Starts
  // Hooks Ends

  return isLoading ? (
    <FormSkeleton withTitle={true} elevation={0} columns={1} inputs={5} />
  ) : (
    <>
      <Typography variant="h1" color="primary" noWrap>
        {t('productsEdit:leftColumn.title')}
      </Typography>
      {product && (
        <List className={classes.resume}>
          <ListItem divider disableGutters>
            <Typography variant="h2">{t('productsEdit:leftColumn.subtitle')}</Typography>
          </ListItem>
          {product.code && (
            <ListItem divider disableGutters>
              <Typography variant="caption">
                {t('productsEdit:leftColumn.captions.code')}
              </Typography>
              <Typography variant="body2">{product.code}</Typography>
            </ListItem>
          )}
          {product.description && (
            <ListItem divider disableGutters>
              <Typography variant="caption">
                {t('productsEdit:leftColumn.captions.description')}
              </Typography>
              <Typography variant="body2">{product.description}</Typography>
            </ListItem>
          )}
          {product.type && (
            <ListItem divider disableGutters>
              <Typography variant="caption">
                {t('productsEdit:leftColumn.captions.type')}
              </Typography>
              <Typography variant="body2">
                {selectOptionValueToLabel(productTypeOptions, product.type)}
              </Typography>
            </ListItem>
          )}
          {product.currency && (
            <ListItem divider disableGutters>
              <Typography variant="caption">
                {t('productsEdit:leftColumn.captions.currency')}
              </Typography>
              <Typography variant="body2">
                {enumTranslateOption(product.currency, EnumCurrency, 'enums:EnumCurrencyLabel')}
              </Typography>
            </ListItem>
          )}
          {product.commodity && (
            <ListItem divider disableGutters>
              <Typography variant="caption">
                {t('productsEdit:leftColumn.captions.commodity')}
              </Typography>
              <Typography variant="body2">
                {enumTranslateOption(product.commodity, EnumCommodity, 'enums:EnumCommodity')}
              </Typography>
            </ListItem>
          )}
          {product.breakdownType && (
            <ListItem divider disableGutters>
              <Typography variant="caption">
                {t('productsEdit:leftColumn.captions.breakdownType')}
              </Typography>
              <Typography variant="body2">
                {enumTranslateOption(
                  product.breakdownType,
                  EnumNivelDesglose,
                  'enums:EnumNivelDesglose'
                )}
              </Typography>
            </ListItem>
          )}
          {product.weightedType && (
            <ListItem divider disableGutters>
              <Typography variant="caption">
                {t('productsEdit:leftColumn.captions.weightedType')}
              </Typography>
              <Typography variant="body2">
                {enumTranslateOption(product.weightedType, EnumProrrateo, 'enums:EnumProrrateo')}
              </Typography>
            </ListItem>
          )}
          {productTemplates && productTemplates.length > 0 && (
            <TemplateListItem
              templates={productTemplates}
              title={t('productsEdit:leftColumn.captions.templates')}
            />
          )}
          {productTemplatesConcepts && productTemplatesConcepts.length > 0 && (
            <TemplateListItem
              templates={productTemplatesConcepts}
              title={t('productsEdit:leftColumn.captions.templateConcepts')}
            />
          )}
        </List>
      )}
      {contractData && (
        <List className={classes.resume}>
          <ListItem divider disableGutters>
            <Typography variant="h2">{t('productsEdit:leftColumn.contractSubtitle')}</Typography>
          </ListItem>
          {contractData.code && (
            <ListItem divider disableGutters>
              <Typography variant="caption">
                {t('productsEdit:leftColumn.captions.contractCode')}
              </Typography>
              <Typography variant="body2">{contractData.code}</Typography>
            </ListItem>
          )}
          {contractData.description && (
            <ListItem divider disableGutters>
              <Typography variant="caption">
                {t('productsEdit:leftColumn.captions.contractDescription')}
              </Typography>
              <Typography variant="body2">{contractData.description}</Typography>
            </ListItem>
          )}
          {contractData.commodity && (
            <ListItem divider disableGutters>
              <Typography variant="caption">
                {t('productsEdit:leftColumn.captions.commodity')}
              </Typography>
              <Typography variant="body2">
                {enumTranslateOption(contractData.commodity, EnumCommodity, 'enums:EnumCommodity')}
              </Typography>
            </ListItem>
          )}
        </List>
      )}
    </>
  )
}
